import { countryCodeToEmoji } from "@/utils/creatorDisplay";
import { useQuery } from "@apollo/client/react";
import { Menu } from "@repo/components/src/ui/menu";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import FormInputMenu from "@repo/components/src/forms/menu";
import { FormInputSelectProps } from "@repo/components/src/forms/types";
import { AvailableCountriesDocument } from "@repo/graphql/documents/country/query.generated";
import { ComponentProps, useMemo } from "react";
import { FieldValues } from "react-hook-form";

type Props<TFieldValues extends FieldValues> = Omit<
  FormInputSelectProps<TFieldValues, string | number | null | undefined>,
  "options"
> & {
  headingProps?: ComponentProps<typeof Heading>;
  menuProps?: Omit<ComponentProps<typeof Menu>, "trigger">;
  buttonTriggerProps?: Omit<
    ComponentProps<typeof Button>,
    "children" | "onPress"
  >;
  buttonTextTriggerProps?: Omit<ComponentProps<typeof ButtonText>, "children">;
  useCompleteButtonWidth?: boolean;
};

/**
 * Country picker shared by onboarding step 1 and the settings "add brand"
 * modal. Options come from `availableCountries` (live countries only), never
 * a hardcoded country list.
 *
 * No searchable combobox in `@repo/components/src/forms` fits a small,
 * in-memory option list: `singleOptionSearch`/`multipleOptionsSearch` are
 * built around a remote paginated Apollo query (`networkStatus`, `pageInfo`,
 * `fetchMore`), not a static list. So this keeps `FormInputMenu` regardless
 * of how many countries are live.
 */
const CountrySelectField = <TFieldValues extends FieldValues>(
  props: Props<TFieldValues>,
) => {
  const { data } = useQuery(AvailableCountriesDocument);
  const options = useMemo(
    () =>
      (data?.countryCity?.availableCountries ?? []).map((country) => ({
        label: `${countryCodeToEmoji(country.code)} ${country.name}`,
        value: country.id,
      })),
    [data],
  );

  return <FormInputMenu {...props} options={options} />;
};

export default CountrySelectField;
